// Get API URLs from environment variables with fallbacks
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://app.bitsport.gg';
export const STREAM_URL = import.meta.env.VITE_STREAM_URL || 'https://app.bitsport.gg'; // Default Stream URL
// Default WS_URL based on environment variables
const defaultWsUrl = import.meta.env.VITE_WS_URL || 'wss://app.bitsport.gg';
export const WS_URL = defaultWsUrl;

// Log the API configuration for debugging
console.log('API Configuration:');
console.log('API_BASE_URL:', API_BASE_URL);
console.log('STREAM_URL:', STREAM_URL);
console.log('WS_URL:', WS_URL);

export const API_ENDPOINTS = {
  auth: {
    login: '/api/auth/login',
    signup: '/api/auth/signup',
    verifyToken: '/api/auth/verify',
    refreshToken: '/api/auth/refresh',
    getMe: '/api/auth/me'
  },
  predictions: {
    create: (streamId) => `/api/streams/${streamId}/predictions`,
    vote: (streamId, predictionId) => `/api/streams/${streamId}/predictions/${predictionId}/vote`,
    end: (streamId, predictionId) => `/api/streams/${streamId}/predictions/${predictionId}/end`,
    get: (streamId) => `/api/streams/${streamId}/predictions`
  },
  admin: {
    users: '/api/admin/users',
    streams: '/api/admin/streams',
    deleteStream: (streamId) => `/api/admin/streams/${streamId}`,
    quests: {
      list: '/api/quests/admin/all',
      create: '/api/quests',
      createAI: '/api/quests/ai',
      update: (id) => `/api/quests/admin/${id}`,
      delete: (id) => `/api/quests/admin/${id}`
    },
    tournaments: {
      list: '/api/tournaments/admin/all',
      create: '/api/tournaments',
      update: (id) => `/api/tournaments/admin/${id}`,
      delete: (id) => `/api/tournaments/admin/${id}`
    },
    tasks: {
      list: '/api/tasks/admin/all',
      create: '/api/tasks',
      update: (id) => `/api/tasks/admin/${id}`,
      delete: (id) => `/api/tasks/admin/${id}`,
      submissions: '/api/tasks/admin/submissions',
      approveSubmission: (id) => `/api/tasks/admin/submissions/${id}/approve`,
      rejectSubmission: (id) => `/api/tasks/admin/submissions/${id}/reject`
    },
    nft: {
      collections: '/api/nft/admin/collections',
      create: '/api/nft/admin/collections',
      update: (id) => `/api/nft/admin/collections/${id}`,
      delete: (id) => `/api/nft/admin/collections/${id}`,
      nfts: '/api/nft/admin/nfts',
      createNFT: '/api/nft/admin/nfts',
      updateNFT: (id) => `/api/nft/admin/nfts/${id}`,
      deleteNFT: (id) => `/api/nft/admin/nfts/${id}`,
      forceDeleteNFT: (id) => `/api/nft/admin/nfts/${id}/force-delete`,
      mint: '/api/nft/admin/nfts/mint',
      uploadNFTImage: '/api/nft/admin/upload/nft-image',
      uploadCollectionBanner: '/api/nft/admin/upload/collection-banner',
      uploadCollectionLogo: '/api/nft/admin/upload/collection-logo'
    }
  },
  presale: {
    transaction: '/api/presale/transaction',
    transactions: '/api/presale/transactions',
    total: '/api/presale/total'
  },
  duel: {
    rooms: '/api/duel/rooms',
    createRoom: '/api/duel/rooms',
    roomDetails: (roomId) => `/api/duel/rooms/${roomId}`,
    joinRoom: (roomId) => `/api/duel/rooms/${roomId}/join`,
    leaveRoom: (roomId) => `/api/duel/rooms/${roomId}/leave`,
    addChatMessage: (roomId) => `/api/duel/rooms/${roomId}/chat`,
    setReady: (roomId) => `/api/duel/rooms/${roomId}/ready`,
    stats: (userId) => `/api/duel/stats/${userId}`,
    leaderboard: '/api/duel/leaderboard',
    history: (userId) => `/api/duel/history/${userId}`,
    startMatch: (matchId) => `/api/duel/matches/${matchId}/start`,
    results: (matchId) => `/api/duel/matches/${matchId}/results`,
    abort: (matchId) => `/api/duel/matches/${matchId}/abort`
  },
  quests: {
    list: '/api/quests',
    create: '/api/quests',
    details: (id) => `/api/quests/${id}`,
    leaderboard: (id) => `/api/quests/${id}/leaderboard`,
    start: (id) => `/api/quests/${id}/start`,
    result: (id) => `/api/quests/${id}/result`
  },
  tasks: {
    list: '/api/tasks',
    details: (id) => `/api/tasks/${id}`,
    submit: (id) => `/api/tasks/${id}/submit`,
    userSubmissions: '/api/tasks/user/submissions'
  },
  streams: {
    list: '/api/streams',
    create: '/api/streams',
    details: (id) => `/api/streams/${id}`,
    key: '/api/streams/key',
    end: (id) => `/api/streams/${id}/end`,
    like: (id) => `/api/streams/${id}/like`,
    follow: (id) => `/api/streams/${id}/follow`
  },
  tournaments: {
    list: '/api/tournaments',
    create: '/api/tournaments',
    details: (id) => `/api/tournaments/${id}`,
    join: (id) => `/api/tournaments/${id}/join`
  },
  wallet: {
    get: (address) => `/api/wallet/${address}`,
    create: '/api/wallet',
    update: '/api/wallet/update',
    updateProfile: '/api/wallet/update-profile', // Endpoint for updating profile without authentication
    directUpdateProfile: '/direct-update-profile', // Direct endpoint that bypasses all middleware
    claim: '/api/wallet/claim',
    reward: '/api/wallet/reward',
    claimToExternal: '/api/wallet/claim-external',
    transactions: (address) => `/api/wallet/${address}/transactions`,
    claimStatus: (claimId) => `/api/wallet/claims/${claimId}/status`,
  },
  // Simplified wallet endpoints for direct access
  WALLET: '/api/wallet',
  TRANSACTIONS: '/api/wallet/transactions',
  CLAIM: '/api/wallet/claim-external',
  users: {
    updateUsername: '/api/users/me/username', // Endpoint for updating the logged-in user's username
    createOrUpdate: '/api/user/create-or-update' // Endpoint for creating or updating a user without authentication
  },
  chat: {
    getMessages: (streamId) => `/api/streams/${streamId}/chat`,
    sendMessage: '/api/streams/chat',
    // Alternative endpoints in case the primary ones don't work
    getMessagesAlt: (streamId) => `/api/chat/${streamId}`,
    sendMessageAlt: '/api/chat/message'
  },
  nft: {
    collections: '/api/nft/collections',
    collection: (id) => `/api/nft/collections/${id}`,
    nfts: (collectionId) => `/api/nft/collections/${collectionId}/nfts`,
    nft: (id) => `/api/nft/nfts/${id}`,
    purchase: '/api/nft/nfts/purchase'
  }
};
